// Live pending join-request state (CYBORG corporate-email onboarding).
//
// A small workspace-scoped store for the admin surfaces: the Members settings
// panel (pending approvals list) and the Settings-tab badge (pending count). It
// mirrors the workspace's join_requests, keyed by id so the live
// `join_request_changed` push upserts in place — the same shape as
// DaemonAccessRequestsState / PageAccessRequestsState.
//
// Scoped to ONE workspace at a time: `workspaceId` records which workspace the
// rows belong to, so a stale push for a workspace the admin has since navigated
// away from is dropped instead of inflating the badge. Seeded on demand by the
// Members panel (listJoinRequests) and kept live by the push handler in
// app.svelte.ts.
import type { WorkspaceJoinRequestView } from "../core/client.js";

export class JoinRequestsState {
  // The requests currently loaded, and which workspace they belong to.
  requests: WorkspaceJoinRequestView[] = $state([]);
  workspaceId: string | null = $state(null);

  // Replace the set for `workspaceId` (a fresh list load). Switching workspace
  // resets `requests` so counts never bleed across workspaces.
  load(workspaceId: string, requests: WorkspaceJoinRequestView[]): void {
    this.workspaceId = workspaceId;
    this.requests = requests;
  }

  // Upsert one row from a live push. Ignored when it belongs to a workspace we
  // are not currently tracking (guards the badge against cross-workspace bleed).
  // A decided (non-pending) request stays in the list so the panel can reflect
  // the transition; the pending selectors below simply exclude it.
  upsert(request: WorkspaceJoinRequestView): void {
    if (this.workspaceId !== null && request.workspaceId !== this.workspaceId) return;
    const idx = this.requests.findIndex((r) => r.id === request.id);
    if (idx === -1) {
      this.requests = [...this.requests, request];
    } else {
      const next = this.requests.slice();
      next[idx] = request;
      this.requests = next;
    }
  }

  // Pending requests for `workspaceId`, newest-first (matches the server order).
  pendingFor(workspaceId: string | undefined): WorkspaceJoinRequestView[] {
    if (!workspaceId || this.workspaceId !== workspaceId) return [];
    return this.requests.filter((r) => r.status === "pending");
  }

  // Count of pending requests for `workspaceId` — the Settings-tab badge source.
  pendingCount(workspaceId: string | undefined): number {
    return this.pendingFor(workspaceId).length;
  }

  clear(): void {
    this.requests = [];
    this.workspaceId = null;
  }
}

export const joinRequestsState = new JoinRequestsState();
